import { useCallback, useEffect, useRef, useState } from "react";
import { ArrowLeft, ArrowRight, BookOpen, CheckCircle2 } from "lucide-react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { toast } from "sonner";
import { useLocale } from "@/lib/i18n";
import { Choice, Repeat, Typed, YesNo } from "@/components/interview/Controls";
import { Drawer } from "./ChatShell";

function show(q, v, pick, t) {
  if (v === null || v === undefined || v === "") return "";
  if (q.type === "yesno") return v ? t("yes") : t("no");
  if (q.type === "choice") return pick((q.options.find((o) => o.value === v) || {}).label) || v;
  if (q.type === "group") return Object.values(v).filter(Boolean).join(", ");
  if (q.type === "repeat") return `${v.length}`;
  return String(v);
}

const Bubble = ({ mine, children, testid }) => (
  <div className={`hv-rise flex ${mine ? "justify-end" : "justify-start"}`}>
    <div data-testid={testid} className={`max-w-[88%] break-words rounded-lg px-4 py-3 text-sm sm:text-base ${mine ? "bg-[#FFF1DA] font-medium text-[#14183E]" : "border border-[#E7E5DF] bg-white text-[#1E1D4C]"}`}>{children}</div>
  </div>
);

const Thinking = ({ text }) => (
  <div className="hv-rise flex justify-start" data-testid="interview-loading">
    <div className="flex items-center gap-3 rounded-lg border border-[#E7E5DF] bg-white px-4 py-3 text-sm text-[#5E6282]">
      <span className="flex gap-1" aria-hidden>{[0, 1, 2].map((i) => <span key={i} className="h-2 w-2 animate-bounce rounded-full bg-[#181E4B]" style={{ animationDelay: `${i * 0.15}s` }} />)}</span>
      {text}
    </div>
  </div>
);

export default function ChatInterview({ caseId, onChanged, onUnavailable, onProgress }) {
  const { t, pick, pk, lang, rtl } = useLocale();
  const [st, setSt] = useState(null);
  const [editId, setEditId] = useState(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);
  const [explain, setExplain] = useState(false);
  const [pending, setPending] = useState(null);
  const end = useRef(null);

  const load = useCallback(() => {
    api.get(`/cases/${caseId}/interview`).then((r) => setSt(r.data)).catch((e) => { if (e.response?.status === 404) onUnavailable(); else setSt(false); });
  }, [caseId, onUnavailable]);
  useEffect(() => { load(); }, [load]);
  useEffect(() => { end.current?.scrollIntoView({ behavior: "smooth", block: "end" }); }, [st?.current_id, editId, pending]);

  const submit = async (q, value) => {
    setBusy(true); setErr(null);
    setPending({ id: q.id, label: show(q, value, pick, t) || "—" });
    try { const r = await api.post(`/cases/${caseId}/interview/answer`, { id: q.id, value }); setSt(r.data); setEditId(null); onChanged?.(); }
    catch (e) { const d = e.response?.data?.detail; setErr(d && typeof d === "object" ? pick(d) : "Could not save"); }
    finally { setBusy(false); setPending(null); }
  };
  const skip = async (q) => {
    setBusy(true); setPending({ id: q.id, label: "—" });
    try { const r = await api.post(`/cases/${caseId}/interview/skip`, { id: q.id }); setSt(r.data); onChanged?.(); }
    catch { toast.error("Could not skip"); } finally { setBusy(false); setPending(null); }
  };

  useEffect(() => {
    if (!st || !onProgress) return;
    const cq = st.questions.find((q) => q.id === (editId || st.current_id));
    const n = Math.min(st.progress.done + 1, st.progress.total);
    onProgress(cq ? { text: `${t("question")} ${n} ${t("of")} ${st.progress.total}`, section: pick(st.sections[cq.section]), pct: Math.round((st.progress.done / Math.max(st.progress.total, 1)) * 100) } : { text: "", section: "", pct: 100 });
  }, [st, editId, lang]); // eslint-disable-line react-hooks/exhaustive-deps

  if (st === false) return <p className="text-sm text-[#5E6282]">Could not load your questions.</p>;
  if (!st) return <div className="h-40 animate-pulse rounded-lg bg-white" />;

  const cur = st.questions.find((q) => q.id === (editId || st.current_id));
  const answered = st.questions.filter((q) => q.status !== "pending");
  const before = answered.filter((q) => q.id !== cur?.id);
  const idx = cur ? answered.findIndex((q) => q.id === cur.id) : -1;
  const prev = cur ? (idx > 0 ? answered[idx - 1] : idx === -1 ? answered[answered.length - 1] : null) : answered[answered.length - 1];
  const Arrow = rtl ? ArrowRight : ArrowLeft;
  const urdu = lang === "ur";

  return (
    <div className="space-y-4" data-testid="interview-panel">
      {before.map((q) => (
        <div key={q.id} className="space-y-3" data-testid={`answered-${q.id}`}>
          <Bubble>{pick(q.text)}</Bubble>
          <Bubble mine testid={`reply-${q.id}`}>
            <span>{q.status === "skipped" ? "—" : show(q, q.value, pick, t)}</span>
            <button onClick={() => { setEditId(q.id); setErr(null); }} className="ms-3 text-xs font-normal text-[#029BC5] underline" data-testid={`edit-${q.id}`}>{t("edit")}</button>
          </Bubble>
        </div>
      ))}

      {pending && (
        <div className="space-y-3">
          <Bubble mine testid="reply-pending">{pending.label}</Bubble>
          <Thinking text={urdu ? "آپ کا جواب محفوظ ہو گیا۔ اگلا سوال آ رہا ہے…" : "Saved. Loading your next question…"} />
        </div>
      )}

      {cur && !pending ? (
        <div key={cur.id} className="hv-rise-delay space-y-3 pb-24" data-testid="question-wrapper">
          {prev && <button data-testid="chat-back-btn" onClick={() => { setEditId(prev.id); setErr(null); }} className="inline-flex items-center gap-1.5 rounded-md border border-[#E7E5DF] bg-white px-3 py-1.5 text-sm"><Arrow size={14} />{lang === "ur" ? "واپس" : "Back"}</button>}
          <section data-testid={`question-${cur.id}`} className="w-full max-w-xl overflow-hidden rounded-lg border border-[#E7E5DF] bg-white">
            <div className="flex items-start justify-between gap-3 bg-[#181E4B] px-5 py-4 text-white">
              <h2 className="text-lg font-semibold leading-snug" data-testid="interview-question-text">{pick(cur.text)}</h2>
              {pick(cur.help) && <button data-testid="explain-step-btn" onClick={() => setExplain(true)} className="flex shrink-0 items-center gap-1.5 rounded-md border border-white/60 px-3 py-1.5 text-xs hover:bg-white/10"><BookOpen size={13} />{urdu ? "اس مرحلے کی وضاحت" : "Explain this step"}</button>}
            </div>
            <div className="p-5">
              {pk && urdu && <p className="mb-3 text-xs text-[#5E6282]" dir="ltr" lang="en">{cur.text.en}</p>}
              {pk && !urdu && cur.text.ur && <p className="mb-3 border-s-2 border-[#F1A501] bg-[#FBFAF7] px-3 py-2 font-urdu text-sm text-[#181E4B]" dir="rtl" lang="ur" data-testid="interview-urdu-help">{cur.text.ur}</p>}
              {cur.type === "choice" && <Choice options={cur.options} value={cur.value} onPick={(v) => submit(cur, v)} />}
              {cur.type === "yesno" && <YesNo value={cur.value} onPick={(v) => submit(cur, v)} />}
              {cur.type === "repeat" && <Repeat key={cur.id} q={cur} value={cur.value} onSubmit={(v) => submit(cur, v)} busy={busy} />}
              {["text", "textarea", "number", "date", "group"].includes(cur.type) && (
                <Typed key={cur.id} q={cur} value={cur.value} onSubmit={(v) => submit(cur, v)} onSkip={() => skip(cur)} busy={busy} />
              )}
              {err && <p className="mt-3 text-sm text-coral" data-testid="interview-error">{err}</p>}
              {editId && editId !== st.current_id && st.current_id && <button onClick={() => setEditId(null)} className="mt-4 text-xs text-[#5E6282] underline" data-testid="edit-cancel">{urdu ? "تازہ سوال پر واپس" : "Back to latest question"}</button>}
            </div>
          </section>
        </div>
      ) : pending ? null : (
        <section data-testid="interview-complete" className="w-full max-w-xl overflow-hidden rounded-lg border border-[#E7E5DF] bg-white">
          <div className="h-1 bg-[#F1A501]" />
          <div className="space-y-5 p-6">
            <h2 className="font-serif text-2xl font-bold text-[#181E4B]">{urdu ? "آپ کی فائل تیار ہے" : "Your file is ready"}</h2>
            <ul className="space-y-2 text-sm">
              {(urdu ? ["آپ کی تفصیلات مکمل ہیں", "فارم کے جوابات تیار ہیں"] : ["Your details are complete", "Form answers are prepared"]).map((x) => <li key={x} className="flex items-center gap-2"><CheckCircle2 size={16} className="text-[#15803D]" />{x}</li>)}
            </ul>
            <div className="rounded-md bg-[#FFF1DA] p-4">
              <div className="font-semibold text-[#14183E]">{urdu ? "اگلا مرحلہ: ہم آپ کی اپائنٹمنٹ بک کریں گے" : "Next: we book your appointment"}</div>
              <p className="mt-1 text-sm text-[#2C304E]">{urdu ? "ہم سرکاری اپائنٹمنٹ سسٹم پر نظر رکھتے ہیں۔ سلاٹ ملتے ہی آپ کو SMS اور واٹس ایپ پر اطلاع ملے گی۔" : "We watch the official appointment system for you. You get a message as soon as a slot is confirmed."}</p>
            </div>
            <Link to={`/app/cases/${caseId}/file`} className="inline-block rounded-md bg-[#181E4B] px-5 py-2.5 text-sm font-semibold text-white" data-testid="interview-to-file">{urdu ? "خطوط اور پرنٹ فائل دیکھیں" : "Review letters and print file"}</Link>
          </div>
        </section>
      )}

      {st.notes.length > 0 && (
        <div className="max-w-xl space-y-3" data-testid="interview-notes" dir="ltr" lang="en">
          {st.notes.map((n) => (
            <div key={n.id} className="border border-[#E7E5DF] border-l-4 border-l-coral bg-white p-4" data-testid={`note-${n.id}`}>
              <p className="text-sm font-medium text-[#1E1D4C]">{n.title}</p><p className="mt-1 text-xs text-[#5E6282]">{n.why} {n.fix}</p>
            </div>
          ))}
        </div>
      )}
      <div ref={end} />
      {explain && cur && (
        <Drawer testid="explain-drawer" title={urdu ? "اس مرحلے کی وضاحت" : "Explain this step"} sub={urdu ? "" : "Holavisto explains only. Your answers never change from here."} onClose={() => setExplain(false)}>
          <p className="leading-relaxed text-[#2C304E]" data-testid="explain-text">{pick(cur.help)}</p>
        </Drawer>
      )}
    </div>
  );
}
